import { AppConfig } from "../config";
import { currentTenant } from "./tenantStorage";

/**
 * @param {string} Url
 * @param {string} method
 * @param {Object|null} payload
 * @param {Object} options
 * @param {Object} options.headers
 * @param {Object} options.params
 * @param {Array} options.projections
 * @param {boolean} options.includeCookies
 * @returns {Promise}
 */
const apiClient = async (Url, method = "GET", payload = null, options = {}) => {
  const baseURL = AppConfig.API_URL;
  try {
    const {
      headers = {},
      params = {},
      projections = null,
      includeCookies = true,
      refresh = true,
      responseType = null,
    } = options;

    if (!baseURL) {
      throw new Error("API_URL is not defined in AppConfig");
    }

    if (Url == null || Url === "" || String(Url) === "undefined") {
      throw new Error("API path is missing");
    }

    let fullUrl;
    if (Url.startsWith("http://") || Url.startsWith("https://")) {
      fullUrl = Url;
    } else {
      const hasTrailingSlash = baseURL.endsWith("/");
      const hasLeadingSlash = Url.startsWith("/");
      if (hasTrailingSlash && hasLeadingSlash) {
        fullUrl = `${baseURL}${Url.substring(1)}`;
      } else if (!hasTrailingSlash && !hasLeadingSlash) {
        fullUrl = `${baseURL}/${Url}`;
      } else {
        fullUrl = `${baseURL}${Url}`;
      }
    }

    let url;
    try {
      url = new URL(fullUrl);
    } catch (error) {
      console.error("Invalid URL:", fullUrl);
      throw new Error(`Invalid URL: ${fullUrl}`);
    }

    const methodUpper = method.toUpperCase();
    let modifiedPayload = payload;
    let modifiedParams = { ...params };

    if (projections && Array.isArray(projections) && projections.length > 0) {
      if (methodUpper === "GET") {
        modifiedParams.projections = JSON.stringify(projections);
      } else {
        modifiedPayload = {
          ...payload,
          projections: projections
        };
      }
    }

    if (Object.keys(modifiedParams).length > 0) {
      Object.entries(modifiedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value);
        }
      });
    }

    const tenant = currentTenant();
    const defaultHeaders = {
      "Content-Type": "application/json",
      ...(tenant ? { "X-Tenant": tenant } : {}),
      ...headers,
    };

    const fetchOptions = {
      method: methodUpper,
      headers: defaultHeaders,
    };

    if (includeCookies) {
      fetchOptions.credentials = "include";
    }

    if (modifiedPayload && methodUpper !== "GET") {
      if (modifiedPayload instanceof FormData) {
        fetchOptions.body = modifiedPayload
        // Remove Content-Type for FormData
        delete fetchOptions.headers["Content-Type"]
      } else {
        fetchOptions.body = JSON.stringify(modifiedPayload)
      }
    }

    const response = await fetch(url.toString(), fetchOptions);

    if (!response.ok && response.status === 401) {
      localStorage.removeItem("user");
      if (refresh) window.location.reload();
    }

    if (responseType === "blob") {
      if (!response.ok) {
        let message = `Request failed (${response.status})`
        try {
          const errJson = await response.json()
          message = errJson?.message || message
        } catch (_) {
          /* ignore */
        }
        throw new Error(message)
      }
      const blob = await response.blob()
      const disposition = response.headers.get("content-disposition") || ""
      const match = disposition.match(/filename="?([^"]+)"?/i)
      return {
        blob,
        filename: match?.[1] || null,
        status: response.status,
      }
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

export default apiClient;